/**
 * PendingInvitations.jsx — Bannière d'invitations en attente
 * 
 * Vérifie au chargement si l'utilisateur a des invitations de partage en attente.
 * Affiche une bannière avec bouton Accepter pour chaque invitation.
 */
import React, { useState, useEffect } from 'react';
import { getAuthHeaders } from '../utils/authFetch';
import { UserPlus, Check, X } from 'lucide-react';

const PendingInvitations = ({ darkMode = false, onAccepted }) => {
  const [invitations, setInvitations] = useState([]);
  const [accepting, setAccepting] = useState(null);
  const headers = { ...getAuthHeaders(), 'Content-Type': 'application/json' };

  const c = {
    card: darkMode ? '#282845' : '#fff',
    text: darkMode ? '#E1E1E1' : '#1a1a2e',
    textSec: darkMode ? '#B8BFC7' : '#6B7280',
    accent: '#1B77F1',
    border: darkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)',
    green: '#10B981', purple: '#7C4DFF',
  };
  const bodyFont = { fontFamily: "'Inter', sans-serif" };

  useEffect(() => {
    const load = async () => {
      try {
        const res = await fetch('/api/shares/pending', { headers });
        const data = await res.json();
        if (data.success && data.invitations.length > 0) {
          setInvitations(data.invitations);
        }
      } catch (e) { /* silent */ }
    };
    load();
  }, []);

  const handleAccept = async (invToken) => {
    setAccepting(invToken);
    try {
      const res = await fetch(`/api/shares/${invToken}/accept`, { method: 'POST', headers });
      const data = await res.json();
      if (data.success) {
        setInvitations(prev => prev.filter(i => i.token !== invToken));
        if (onAccepted) onAccepted();
      }
    } catch (e) { /* silent */ }
    finally { setAccepting(null); }
  };

  const handleDismiss = (invToken) => {
    setInvitations(prev => prev.filter(i => i.token !== invToken));
  };

  if (invitations.length === 0) return null;

  return (
    <div style={{ marginBottom: '16px' }}>
      {invitations.map(inv => (
        <div key={inv._id} style={{
          background: darkMode ? 'rgba(124,77,255,0.12)' : '#F3EEFF',
          borderRadius: '12px', padding: '16px 20px', marginBottom: '8px',
          border: `1px solid ${darkMode ? 'rgba(124,77,255,0.25)' : '#E0D4FF'}`,
          display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap'
        }}>
          <UserPlus size={20} color={c.purple} />
          <div style={{ flex: 1, minWidth: 200 }}>
            <div style={{ ...bodyFont, fontSize: '14px', fontWeight: '600', color: c.text }}>
              Invitation de {inv.ownerName}
            </div>
            <div style={{ ...bodyFont, fontSize: '12px', color: c.textSec }}>
              Accéder à {inv.horseName} en tant que {inv.guestRole === 'veterinaire' ? 'vétérinaire' : 'personne de confiance'}
            </div>
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button onClick={() => handleAccept(inv.token)} disabled={accepting === inv.token}
              style={{
                ...bodyFont, padding: '8px 16px', borderRadius: '8px', border: 'none',
                background: c.green, color: '#fff', fontSize: '12px', fontWeight: '600',
                cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px',
                opacity: accepting === inv.token ? 0.6 : 1
              }}>
              <Check size={14} /> Accepter
            </button>
            <button onClick={() => handleDismiss(inv.token)}
              style={{
                ...bodyFont, padding: '8px', borderRadius: '8px', border: `1px solid ${c.border}`,
                background: 'none', color: c.textSec, cursor: 'pointer'
              }}>
              <X size={14} />
            </button>
          </div>
        </div>
      ))}
    </div>
  );
};

export default PendingInvitations;
